import { useEffect, useState } from 'react';

import { AvatarCropper } from '@/components/AvatarCropper';
import { saveMessage } from '@/lib/apps';
import { useAuth } from '@/lib/auth';
import { useMySessions } from '@/lib/passkeys';
import { useMyAvatar, useUpdateProfile } from '@/lib/profile';

/**
 * Who you are, as this admin knows you.
 *
 * One card, in the design's shape: the person, then the three fields about
 * them in a grid that wraps, then the sentence explaining why two of the three
 * refuse to take anything.
 */
export default function ProfilePage() {
  const { user } = useAuth();
  const update = useUpdateProfile();
  const sessions = useMySessions();

  const avatar = useMyAvatar();
  /*
   * The file somebody just chose, held while they decide which square of it to
   * keep. Null closes the cropper — including after a save, so the dialog does
   * not sit open over the photo it just set.
   */
  const [chosen, setChosen] = useState<File | null>(null);

  const [name, setName] = useState(user?.name ?? '');
  useEffect(() => setName(user?.name ?? ''), [user?.name]);

  const trimmed = name.trim();
  const dirty = Boolean(user) && trimmed !== '' && trimmed !== user?.name;

  if (!user) {
    return (
      <p className="m-0 text-[13px]" style={{ color: 'var(--muted)' }}>
        Loading…
      </p>
    );
  }

  /*
   * The design's second line is "owner · signed in since 8:12", and both halves
   * are real: the role comes from the account, and the time is when this
   * session started, which `mySessions` already carries. Until it arrives the
   * role stands alone rather than the line jumping from one shape to another.
   */
  const here = (sessions.data ?? []).find((session) => session.current);
  const since = here ? ` · signed in since ${startedLabel(here.startedAt)}` : '';

  return (
    <div className="panel flex flex-col gap-2.5 px-4 py-3.5">
      <span className="flex flex-wrap items-center gap-2.5">
        <h2 className="eyebrow m-0">Profile</h2>
      </span>

      <div className="flex items-center gap-3">
        {/*
          The photo, or the initial. Both are `aria-hidden`: the name is written
          out immediately to the right, so anything here is that name read
          twice.
        */}
        {user.avatarUrl ? (
          <img
            aria-hidden="true"
            src={user.avatarUrl}
            alt=""
            className="h-[38px] w-[38px] flex-none rounded-full object-cover"
            style={{ border: '1px solid var(--line2)' }}
          />
        ) : (
          <span
            aria-hidden="true"
            className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-full text-sm font-semibold"
            style={{ background: 'var(--in)', border: '1px solid var(--line2)' }}
          >
            {(user.name?.trim() || user.email)[0]?.toUpperCase()}
          </span>
        )}
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-[13px] font-semibold">{user.name || user.email}</span>
          <span className="truncate font-mono text-[10px]" style={{ color: 'var(--dim)' }}>
            {user.role.toLowerCase()}
            {since}
          </span>
        </span>
        <span className="ml-auto flex flex-none items-center gap-2">
          {user.avatarUrl ? (
            <button
              type="button"
              disabled={avatar.isPending}
              onClick={() => avatar.mutate(null)}
              className="btn-quiet focus-ring cursor-pointer text-[11.5px] disabled:cursor-default disabled:opacity-40"
              style={{ color: 'var(--dim)' }}
            >
              Remove
            </button>
          ) : null}
          {/*
            The label is the button: a `<label>` around a hidden file input is
            what opens a picker without a click handler that has to find the
            input first.
          */}
          <label className="btn-small focus-ring cursor-pointer">
            {user.avatarUrl ? 'Change photo' : 'Add photo'}
            <input
              type="file"
              className="sr-only"
              accept="image/png,image/jpeg,image/webp"
              disabled={avatar.isPending}
              onChange={(event) => {
                /*
                 * Null when the picker was closed with Cancel, which fires a
                 * change with no file — and null is what closes the cropper, so
                 * the two cases are the same statement rather than a guard that
                 * decides nothing.
                 */
                const file = event.target.files?.[0] ?? null;
                /*
                 * Cleared straight away, so choosing the same file twice in a
                 * row still fires a change — which is what happens when
                 * somebody cancels the cropper and picks the same photo again.
                 *
                 * Knowingly unguarded: jsdom will not let a file input's value
                 * be set to anything but the empty string, so no test can put
                 * it in the state this line clears.
                 */
                event.target.value = '';
                setChosen(file);
              }}
            />
          </label>
        </span>
      </div>

      {avatar.isError ? (
        <span className="text-[11.5px] leading-[1.5]" role="alert" style={{ color: 'var(--danger)' }}>
          {saveMessage(avatar.error)}
        </span>
      ) : null}

      {chosen ? (
        <AvatarCropper
          file={chosen}
          saving={avatar.isPending}
          onCancel={() => setChosen(null)}
          onSave={(cropped) =>
            avatar.mutate(cropped, {
              // Closed on success only: a refusal leaves the dialog open over
              // the photo it refused, with the reason underneath it.
              onSuccess: () => setChosen(null),
            })
          }
        />
      ) : null}

      {/*
        A grid that fits as many 180px columns as the panel has room for, which
        is what the design asks for. Three fields side by side on a wide screen,
        one under another on a narrow one — and Name no longer takes a full row
        to itself while the other two share the next.
      */}
      <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
        <label className="flex min-w-0 flex-col gap-1.5">
          <span className="field-label">Name</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="field w-full"
          />
        </label>
        <label className="flex min-w-0 flex-col gap-1.5">
          {/*
            The design puts what the address is *for* in the label. Read-only
            rather than absent: it is how somebody signs in, so they need to see
            it and copy it.
          */}
          <span className="field-label">Email · used to sign in</span>
          <input
            value={user.email}
            readOnly
            aria-readonly
            aria-label="Email · used to sign in"
            className="field field-mono w-full read-only:cursor-default read-only:opacity-60"
          />
        </label>
        <label className="flex min-w-0 flex-col gap-1.5">
          <span className="field-label">Role</span>
          <input
            value={user.role}
            readOnly
            aria-readonly
            className="field w-full read-only:cursor-default read-only:opacity-60"
          />
        </label>
      </div>

      <span className="text-[11.5px] leading-[1.5]" style={{ color: 'var(--dim)' }}>
        Changing the address needs the new one proved first — ask whoever runs the database. The
        role decides nothing yet.
      </span>

      <span className="flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          disabled={!dirty || update.isPending}
          onClick={() => update.mutate(trimmed)}
          className="btn-accent focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
        >
          {update.isPending ? 'Saving…' : 'Save profile'}
        </button>
        {update.isSuccess && !dirty ? (
          <span className="text-[11.5px]" style={{ color: 'var(--ok)' }}>
            Saved.
          </span>
        ) : null}
      </span>

      {update.isError ? (
        <p className="m-0 text-[11.5px]" role="alert" style={{ color: 'var(--danger)' }}>
          {saveMessage(update.error)}
        </p>
      ) : null}
    </div>
  );
}

/** The clock time if it started today, the date if it did not. */
const startedLabel = (iso: string): string => {
  const at = new Date(iso);
  const sameDay = at.toDateString() === new Date().toDateString();
  return sameDay
    ? new Intl.DateTimeFormat('en-GB', { hour: 'numeric', minute: '2-digit', hour12: false }).format(at)
    : new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(at);
};
